import _ from "lodash";
import React, { useState, useRef, useEffect, useCallback } from "react";

import { SearchInput, EmptyState } from "@toddle-edu/ds-web";
import { NoSearchInFilterIllustration } from "@toddle-edu/ds-theme";

import { useHandleScroll } from "Utils/CustomHooks";

import { useI18n } from "Hooks";
import { useMetadata } from "AppComponents/DualPanelManageMembersV2/components/MetadataProvider";

import DynamicList from "./DynamicList";
import CohortListSkeleton from "./CohortListSkeleton";
import classes from "./UsersAndCohorts.scss";
import { DUAL_PANEL_VIEW } from "../../module/utils";

const getViewModeLabel = ({ typeFilter, t }) => {
  const view = DUAL_PANEL_VIEW[typeFilter];
  const label = _.get(view, "labelKey");
  return label ? t(label) : "";
};

const EmptySearch = ({ searchText, typeFilter }) => {
  const { t } = useI18n(["common"]);

  const label = getViewModeLabel({ typeFilter, t });
  const title = searchText
    ? t("common:search_results_not_found")
    : t("common:no_items_found", { item: label });
  const subtitle = searchText
    ? t("common:please_try_a_different_keyword")
    : t("common:no_options_for_filter", { label });

  return (
    <div className={classes.emptyStateContainer}>
      <div className={classes.emptyState}>
        <EmptyState
          dsVersion="2.0"
          illustration={NoSearchInFilterIllustration}
          title={title}
          subtitle={subtitle}
        />
      </div>
    </div>
  );
};

const isActivePaginatingCohort = ({ cohortParam, collapsedCohorts }) => {
  return (
    cohortParam.hasNextPage &&
    !collapsedCohorts[cohortParam.id] &&
    !_.isEmpty(cohortParam.cohorts)
  );
};

const getScrollParams = ({ cohortParamsMap, collapsedCohorts = {} }) => {
  const pickParams = ["networkStatus", "loadMoreFeed", "hasNextPage"];

  const sortedCohorts = _.sortBy(cohortParamsMap, "index");
  const paginatingCohort = _.find(sortedCohorts, cohortParam =>
    isActivePaginatingCohort({ cohortParam, collapsedCohorts })
  );
  if (paginatingCohort) return _.pick(paginatingCohort, pickParams);

  return {
    networkStatus: 1,
    loadMoreFeed: _.noop,
    hasNextPage: false,
  };
};

// Returns the max cohort index to render (-1 = empty state, Infinity = show all including members)
const getMaxVisibleCohortIndex = ({
  cohortParamsMap,
  collapsedCohorts = {},
}) => {
  const hasAnyData = _.some(
    cohortParamsMap,
    cohortParam =>
      !_.isEmpty(cohortParam.cohorts) || !_.isEmpty(cohortParam.userNodeList)
  );

  if (!hasAnyData) {
    return -1; // empty state
  }

  const sortedCohorts = _.sortBy(cohortParamsMap, "index");
  // Only cap render index on cohorts that have data AND still paginating.
  // Cohorts with empty cohorts array are still on initial load — don't block others.
  const paginatingCohort = _.find(sortedCohorts, cohortParam =>
    isActivePaginatingCohort({ cohortParam, collapsedCohorts })
  );

  return paginatingCohort ? paginatingCohort.index : Infinity;
};

export const UsersAndCohortsList = ({
  searchText = "",
  cohortParamsMap = {},
  cohortState = {},
  selectedUsersState = {},
  onAddUsers,
  onRemoveUsers,
  onAddCohort,
  onRemoveCohort,
  prevUsers = [],
  customQueryWrapperFields,
  testId,
}) => {
  const typeFilter = customQueryWrapperFields?.typeFilter;
  const { t } = useI18n(["common"]);

  const [collapsedCohorts, setCollapsedCohorts] = useState({});

  const handleToggleCollapse = useCallback(id => {
    setCollapsedCohorts(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const containerRef = useRef();

  const maxVisibleCohortIndex = getMaxVisibleCohortIndex({
    cohortParamsMap,
    collapsedCohorts,
  });

  useEffect(() => {
    containerRef?.current?.scrollTo({ top: 0 });
  }, [typeFilter, searchText]);

  const componentTestId = testId ? `${testId}-usersAndCohortsList` : undefined;
  const { networkStatus, loadMoreFeed, hasNextPage } = getScrollParams({
    cohortParamsMap,
    collapsedCohorts,
  });

  const isFetchingMore = _.includes([3], networkStatus);

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  if (maxVisibleCohortIndex === -1) {
    const isAnyLoading = _.some(cohortParamsMap, cohort =>
      _.includes([1, 2], cohort.networkStatus)
    );
    if (isAnyLoading) return <CohortListSkeleton />;
    return <EmptySearch searchText={searchText} typeFilter={typeFilter} />;
  }

  const sortedCohortParams = _.sortBy(cohortParamsMap, "index");

  return (
    <div className={classes.usersAndCohortsList} ref={containerRef}>
      {_.map(sortedCohortParams, cohortParams => {
        if (cohortParams.index > maxVisibleCohortIndex) return null;

        const { type, cohorts = [], id } = cohortParams;

        if (_.isEmpty(cohorts)) return null;
        return (
          <DynamicList
            key={id || type}
            params={cohortParams}
            selectedState={cohortState[type]}
            selectedUsersState={selectedUsersState}
            onAddUsers={onAddUsers}
            onRemoveUsers={onRemoveUsers}
            onAddCohort={onAddCohort}
            onRemoveCohort={onRemoveCohort}
            prevUsers={prevUsers}
            testId={componentTestId}
            isCollapsed={!!collapsedCohorts[id]}
            onToggleCollapse={() => handleToggleCollapse(id)}
          />
        );
      })}
      {isFetchingMore ? <CohortListSkeleton compact /> : null}
    </div>
  );
};

// DefaultQueryWrapper is a function that returns null to prevent error
const defaultQueryWrapper = () => null;

const UsersAndCohorts = props => {
  // The above UsersAndCohortsList component is imported and rendered
  // directly in the renderQueryWrapper's returned Component
  const {
    renderQueryWrapper = defaultQueryWrapper,
    testId,
    customQueryWrapperFields,
    filterComponent,
    ...restProps
  } = props;
  const { t } = useI18n(["common", "integration", "announcement"]);

  const { getSearchPlaceholder } = useMetadata();
  const { typeFilter } = customQueryWrapperFields;

  const componentTestId = testId ? `${testId}-userAndCohorts` : undefined;
  const [searchText, setSearchText] = useState("");

  return (
    <div className={classes.rootContainer}>
      <div className={classes.searchContainer}>
        <SearchInput
          dsVersion="2.0"
          onChange={setSearchText}
          placeholder={getSearchPlaceholder({
            t,
            typeFilter,
            customQueryWrapperFields,
          })}
          testId={componentTestId}
          autoFocus
          focusSearchBySlash
        />
        {filterComponent}
      </div>

      <div className={classes.cohortsAndUsersListContainer}>
        {renderQueryWrapper({
          ...restProps,
          searchText,
          isLoading: false,
          isData: true,
          customQueryWrapperFields,
        })}
      </div>
    </div>
  );
};

export default UsersAndCohorts;
